/**
 * Settings → Bot: rename the chat bot and give it a profile picture. The
 * picture is resized client-side (256px JPEG) and stored on the bot's user
 * record, so chat threads, alerts and the floating bubble all pick it up.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bot, Loader2, RotateCcw, Upload } from "lucide-react";
import api from "@/lib/api";

const resize = (file, size = 256) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const c = document.createElement("canvas");
    c.width = c.height = size;
    const ctx = c.getContext("2d");
    const s = Math.min(img.width, img.height);
    ctx.drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, size, size);
    URL.revokeObjectURL(url);
    resolve(c.toDataURL("image/jpeg", 0.85));
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Not an image")); };
  img.src = url;
});

export default function BotPanel() {
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/ai/bot-profile").then((r) => { setProfile(r.data); setName(r.data.name); }).catch(() => {});
  }, []);

  const save = async (body) => {
    setSaving(true);
    try {
      const r = await api.put("/ai/bot-profile", body);
      setProfile(r.data); setName(r.data.name);
      toast.success(body.name !== undefined ? `The bot is now called ${r.data.name}` : body.picture ? "Bot picture updated" : "Back to the default picture");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const pick = async (file) => {
    if (!file) return;
    try { await save({ picture: await resize(file) }); }
    catch { toast.error("That file isn't an image"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!profile) return null;
  const isDefault = profile.picture === profile.default_picture;
  return (
    <section className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="settings-bot-panel">
      <div className="flex items-start gap-4">
        <div className="relative shrink-0">
          <img src={profile.picture} alt={profile.name} data-testid="bot-picture"
            className="h-20 w-20 rounded-full border border-white/15 bg-white object-cover" />
          <Bot className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-[#1a3fd1] p-0.5 text-white" />
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">Chat bot</p>
            <p className="text-xs text-white/60">This is the helper that answers questions in chat and sends the automated alerts. Give it your own name and face.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} data-testid="bot-name-input"
              onKeyDown={(e) => e.key === "Enter" && name.trim() && name !== profile.name && save({ name })}
              className="w-56 rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white focus:border-[#4f6fe8] focus:outline-none" />
            <button type="button" onClick={() => save({ name })} disabled={saving || !name.trim() || name === profile.name} data-testid="bot-name-save"
              className="rounded-md bg-[#1a3fd1] px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white disabled:opacity-40">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save name"}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="bot-picture-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={saving} data-testid="bot-picture-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white/80 hover:border-[#1a3fd1] hover:text-white disabled:opacity-40">
              <Upload className="h-3.5 w-3.5" /> Choose picture
            </button>
            {!isDefault && (
              <button type="button" onClick={() => save({ picture: "" })} disabled={saving} data-testid="bot-picture-reset"
                className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white/60 hover:text-white disabled:opacity-40">
                <RotateCcw className="h-3.5 w-3.5" /> Default picture
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
